import { useId } from "react";
import { Button, Icon, cn } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { useScriptDrawerLayoutStore } from "@/features/persona-opportunities/stores/script-drawer-layout.store";

const RAIL_BUTTON_CLASS =
  "shrink-0 min-h-11 min-w-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-7 [@media(hover:hover)_and_(pointer:fine)]:min-w-7";

export interface VersionsRailState {
  id: string;
  isHidden: boolean;
  isCollapsed: boolean;
  toggle: () => void;
}

/**
 * Rail state for the history view: hidden only when there are no versions, and collapsible
 * only in the drawer (the card surface never collapses).
 */
export function useVersionsRail(drawer: boolean, versionCount: number): VersionsRailState {
  const id = useId();
  const isVersionsRailCollapsed = useScriptDrawerLayoutStore(
    (state) => state.isVersionsRailCollapsed,
  );
  const toggle = useScriptDrawerLayoutStore((state) => state.toggleVersionsRail);
  return {
    id,
    isHidden: versionCount < 1,
    isCollapsed: drawer && isVersionsRailCollapsed,
    toggle,
  };
}

/** Two-column history grid; the drawer animates the rail between open and collapsed. */
export function versionsRailGridClass(drawer: boolean, rail: VersionsRailState): string {
  if (rail.isHidden) {
    return "grid min-h-0 flex-1 grid-cols-1";
  }
  const { isCollapsed } = rail;
  return cn(
    "grid min-h-0 flex-1 grid-cols-1",
    drawer &&
      "transition-[grid-template-columns] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none",
    drawer && (isCollapsed ? "duration-200" : "duration-300"),
    isCollapsed
      ? "md:grid-cols-[minmax(0,1fr)_56px]"
      : drawer
        ? "md:grid-cols-[minmax(0,1fr)_320px]"
        : "md:grid-cols-[minmax(0,1fr)_280px]",
  );
}

/** Pins open-rail content to its final width so text does not reflow while the column grows. */
export const OPEN_RAIL_CONTENT_CLASS = "md:w-80";

export const OPEN_RAIL_CLASS = "animate-in fade-in duration-300 motion-reduce:animate-none";

export function VersionsRailCollapseButton({ rail }: { rail: VersionsRailState }) {
  const { t } = useTranslation();
  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className={RAIL_BUTTON_CLASS}
      aria-label={t("personaOpportunities.scripts.history.collapseVersions", "Hide versions")}
      aria-expanded
      aria-controls={rail.id}
      onClick={rail.toggle}
    >
      <Icon name="panel-right-close" size="sm" color="inherit" />
    </Button>
  );
}

export function CollapsedVersionsRail({
  rail,
  versions,
  activeVersionId,
  onSelect,
}: {
  rail: VersionsRailState;
  versions: { id: string; version: number }[];
  activeVersionId: string;
  onSelect: (version: number) => void;
}) {
  const { t } = useTranslation();
  const selectLabel = t("personaOpportunities.scripts.history.select", "Select version");
  const [latest, ...previous] = versions;

  return (
    <div id={rail.id} className="flex min-h-0 min-w-0 flex-col overflow-hidden">
      <div className="flex min-h-0 flex-1 flex-col items-center gap-3 overflow-x-hidden overflow-y-auto pt-4 pb-3 animate-in fade-in duration-200 motion-reduce:animate-none md:w-14 md:self-end">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={RAIL_BUTTON_CLASS}
          aria-label={t("personaOpportunities.scripts.history.expandVersions", "Show versions")}
          aria-expanded={false}
          aria-controls={rail.id}
          onClick={rail.toggle}
        >
          <Icon name="panel-right-open" size="sm" color="inherit" />
        </Button>
        <span className="flex flex-col items-center gap-0.5 text-caption font-medium text-muted-foreground tabular-nums">
          <Icon name="history" size="xs" color="muted" aria-hidden />
          {versions.length}
        </span>
        {latest ? (
          <div className="flex flex-col items-center gap-1.5 border-t border-border pt-3">
            <span className="text-[10px] font-medium tracking-label text-muted-foreground uppercase">
              {t("personaOpportunities.scripts.history.latest", "Latest")}
            </span>
            <RailVersionButton
              entry={latest}
              isActive={latest.id === activeVersionId}
              selectLabel={selectLabel}
              onSelect={onSelect}
            />
            {previous.length > 0 ? (
              <ol
                aria-label={t(
                  "personaOpportunities.scripts.history.previousVersions",
                  "Previous versions",
                )}
                className="mt-1.5 flex flex-col items-center gap-1.5 border-t border-border pt-3"
              >
                {previous.map((entry) => (
                  <li key={entry.id}>
                    <RailVersionButton
                      entry={entry}
                      isActive={entry.id === activeVersionId}
                      selectLabel={selectLabel}
                      onSelect={onSelect}
                    />
                  </li>
                ))}
              </ol>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function RailVersionButton({
  entry,
  isActive,
  selectLabel,
  onSelect,
}: {
  entry: { version: number };
  isActive: boolean;
  selectLabel: string;
  onSelect: (version: number) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(entry.version)}
      aria-current={isActive ? "true" : undefined}
      aria-label={`${selectLabel} ${entry.version}`}
      className={cn(
        "flex min-h-11 w-10 cursor-pointer items-center justify-center rounded-sm border text-caption font-semibold tabular-nums transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 [@media(hover:hover)_and_(pointer:fine)]:min-h-8",
        isActive
          ? "border-ai bg-ai-subtle text-foreground"
          : "border-transparent text-muted-foreground hover:bg-[color-mix(in_srgb,var(--foreground)_6%,transparent)]",
      )}
    >
      v{entry.version}
    </button>
  );
}
